import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { initiatePartnerAccountTransfer } from 'api/internaltools';
import BulkTransferModal from 'modules/partner-account-transfer/BulkTransferModal';
import {
  CURRENCY_CODE_PATTERN,
  MAX_CURRENCY_CODE_LENGTH,
  SUPPORTED_PARTNERS,
} from 'modules/partner-account-transfer/constants/constants';
import { showError, showToast } from 'utils/notifications';

export default function PartnerAccountTransfer() {
  const [selectedPartnerCode, setSelectedPartnerCode] = useState('');
  const [sourceAccountNumber, setSourceAccountNumber] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [beneficiaryAccountNumber, setBeneficiaryAccountNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [purpose, setPurpose] = useState('');
  const [reference, setReference] = useState('');
  const [createCreditRecord, setCreateCreditRecord] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  const toast = useToast();
  const history = useHistory();

  const isSubmitDisabled =
    !selectedPartnerCode ||
    !sourceAccountNumber.trim() ||
    !beneficiaryAccountNumber.trim() ||
    !amount ||
    !currency.trim() ||
    !purpose.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitDisabled) {
      showToast(toast, 'Error', 'Please fill in all required fields', 'error');

      return;
    }

    const parsedAmount = parseFloat(amount);

    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      showToast(toast, 'Error', 'Amount must be a positive number', 'error');

      return;
    }

    const normalizedCurrency = currency.trim().toUpperCase();

    if (!CURRENCY_CODE_PATTERN.test(normalizedCurrency)) {
      showToast(toast, 'Error', 'Currency must be a 3-6 character asset code, for example USD or USDT', 'error');

      return;
    }

    try {
      setIsSubmitting(true);
      const payload: any = {
        source_account_number: sourceAccountNumber.trim(),
        beneficiary_account_number: beneficiaryAccountNumber.trim(),
        amount: parsedAmount,
        currency: normalizedCurrency,
        purpose: purpose.trim(),
        create_credit_record: createCreditRecord,
      };

      if (beneficiaryName.trim()) {
        payload.beneficiary_name = beneficiaryName.trim();
      }

      if (reference.trim()) {
        payload.reference = reference.trim();
      }

      await initiatePartnerAccountTransfer(selectedPartnerCode, payload);
      showToast(toast, 'Success', 'Partner account transfer initiated.', 'success');
      history.goBack();
    } catch (error) {
      showError(toast, 'Error initiating partner account transfer', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box w='100%' py={8} px={6}>
      <HStack mb={6} spacing={10} align='center'>
        <Button colorScheme='gray' onClick={() => history.goBack()} size='md'>
          Back
        </Button>
        <Text as='h1' flex='1' textAlign='center' fontSize='2xl' fontWeight='bold'>
          Partner Account Transfer
        </Text>
        <Button colorScheme='blue' variant='outline' size='md' onClick={() => setIsBulkModalOpen(true)}>
          Bulk Transfer
        </Button>
      </HStack>

      {isBulkModalOpen && <BulkTransferModal isOpen={isBulkModalOpen} onClose={() => setIsBulkModalOpen(false)} />}

      <Box maxW='640px' mx='auto'>
        <Text mb={6} color='gray.600' fontSize='sm'>
          Move funds out of a partner-held account directly. The operation must originate from the partner balance
          rather than a merchant payout.
        </Text>

        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align='stretch'>
            <FormControl isRequired>
              <FormLabel>Partner</FormLabel>
              <Select
                placeholder='Select partner'
                value={selectedPartnerCode}
                onChange={(e) => setSelectedPartnerCode(e.target.value)}
              >
                {SUPPORTED_PARTNERS.map((partner) => (
                  <option key={partner.code} value={partner.code}>
                    {partner.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Source account number</FormLabel>
              <Input
                value={sourceAccountNumber}
                onChange={(e) => setSourceAccountNumber(e.target.value)}
                placeholder='Partner-held account or IBAN to debit'
              />
            </FormControl>

            <FormControl>
              <FormLabel>Beneficiary name</FormLabel>
              <Input
                value={beneficiaryName}
                onChange={(e) => setBeneficiaryName(e.target.value)}
                placeholder='Beneficiary name (optional)'
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Beneficiary account number</FormLabel>
              <Input
                value={beneficiaryAccountNumber}
                onChange={(e) => setBeneficiaryAccountNumber(e.target.value)}
                placeholder='IBAN or beneficiary bank account number'
              />
            </FormControl>

            <HStack spacing={4} align='end'>
              <FormControl isRequired flex='1'>
                <FormLabel>Amount</FormLabel>
                <Input
                  type='number'
                  step='0.01'
                  min='0'
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder='Enter amount'
                />
              </FormControl>
              <FormControl isRequired maxW='140px'>
                <FormLabel>Currency</FormLabel>
                <Input
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  placeholder='USD'
                  maxLength={MAX_CURRENCY_CODE_LENGTH}
                />
              </FormControl>
            </HStack>

            <FormControl isRequired>
              <FormLabel>Purpose</FormLabel>
              <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder='Purpose of transfer' />
            </FormControl>

            <FormControl>
              <FormLabel>Reference / Notes</FormLabel>
              <Input
                value={reference}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReference(e.target.value)}
                placeholder='Optional reference, ticket ID, or internal notes'
              />
            </FormControl>

            <FormControl>
              <Checkbox
                isChecked={createCreditRecord}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateCreditRecord(e.target.checked)}
              >
                Create credit record for Varni Tech Merchant
              </Checkbox>
              <FormHelperText>
                Books a corresponding credit record against the Varni Tech merchant for this transfer.
              </FormHelperText>
            </FormControl>

            <Button type='submit' colorScheme='blue' size='lg' isLoading={isSubmitting} isDisabled={isSubmitDisabled}>
              Initiate Transfer
            </Button>
          </VStack>
        </form>
      </Box>
    </Box>
  );
}
